import React from "react";
import { Box, Typography, Autocomplete, TextField } from "@mui/material";
import { colors, fonts } from "../../../../constants";
import FormFieldLabel from "../FormFieldLabel";
import {
  getSelectStyle,
  getHelperTextStyles
} from "../../../../utils/formUtils";
import {
  formatProjectNameDescription,
  projectStoredValue
} from "../../../../utils/projectsUtils";

/** Stored/submitted value: description, else name (matches projectStoredValue when description set) */
const projectPrimaryText = (p) => projectStoredValue(p);

const projectOptionLabel = (option) => ` ${option.description}`;

const findProjectByFormValue = (projects, formProjectName) => {
  const raw = (formProjectName || "").trim().toLowerCase();
  if (!raw || !projects?.length) return null;
  return (
    projects.find((p) => (p.name || "").toLowerCase() === raw) ||
    projects.find((p) => (p.description || "").toLowerCase() === raw) ||
    null
  );
};

/**
 * ProjectField Component
 * Project selection field
 */
const ProjectField = ({
  formData,
  errors,
  focusedFields,
  projects,
  loadingProjects,
  isRegionSelectionEnabled,
  handleChange,
  handleFocus,
  handleBlur
}) => {
  const helperTextBaseStyles = getHelperTextStyles();

  const getSelectStyleWithFocus = (field, hasError) => {
    const isFocused = focusedFields[field];
    return getSelectStyle(field, hasError, isFocused);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <FormFieldLabel label="Project Name" required={true} />
      <Autocomplete
        options={projects}
        getOptionLabel={(option) => projectOptionLabel(option)}
        isOptionEqualToValue={(option, value) => {
          const optionId = option?.id || option?._id;
          const valueId = value?.id || value?._id;
          if (optionId && valueId) return String(optionId) === String(valueId);
          return projectPrimaryText(option) === projectPrimaryText(value);
        }}
        value={findProjectByFormValue(projects, formData.projectName)}
        onChange={(event, newValue) => {
          handleChange("projectName")({ target: { value: newValue?.description || "" } });
          handleChange("projectCode")({ target: { value: newValue?.code || "" } });
        }}
        onFocus={handleFocus("projectName")}
        onBlur={handleBlur("projectName")}
        disabled={
          loadingProjects || (isRegionSelectionEnabled && !formData.region)
        }
        loading={loadingProjects}
        openOnFocus
        filterOptions={(options, params) => {
          const searchTerm = params.inputValue.toLowerCase();
          const filtered = options.filter(
            (option) =>
              projectOptionLabel(option).toLowerCase().includes(searchTerm) ||
              projectPrimaryText(option).toLowerCase().includes(searchTerm) ||
              (option.name || "").toLowerCase().includes(searchTerm) ||
              (option.description || "").toLowerCase().includes(searchTerm) ||
              (option.code || "").toLowerCase().includes(searchTerm) ||
              (option.region || "").toLowerCase().includes(searchTerm)
          );
          return filtered;
        }}
        slotProps={{
          popper: {
            sx: {
              /* Dropdown is portaled; option styles must live here, not on Autocomplete root sx */
              "& .MuiAutocomplete-option": {
                borderBottom: `1px solid ${colors.borderDefault}`,
                "&:last-of-type": {
                  borderBottom: "none"
                },
                "&.Mui-focused": {
                  backgroundColor: colors.primaryTint
                },
                '&[aria-selected="true"]': {
                  backgroundColor: colors.primaryTintHover,
                  "&.Mui-focused": {
                    backgroundColor: colors.primaryTintHover
                  }
                }
              }
            }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              readOnly: false,
              autoComplete: "off"
            }}
            placeholder={
              loadingProjects
                ? "Loading projects..."
                : isRegionSelectionEnabled && !formData.region
                  ? "Select region first"
                  : "Type to search project..."
            }
            error={!!errors.projectName}
            sx={{
              "& .MuiOutlinedInput-root": {
                ...getSelectStyleWithFocus("projectName", !!errors.projectName),
                padding: "4px 9px",
                "& fieldset": {
                  border: "none"
                },
                "&:hover fieldset": {
                  border: "none"
                },
                "&.Mui-focused fieldset": {
                  border: "none"
                },
                "&.Mui-disabled": {
                  opacity: 0.6,
                  cursor: "not-allowed"
                }
              },
              "& .MuiInputBase-input": {
                padding: "8px 4px",
                fontSize: fonts.fontSizeMd,
                cursor: "text"
              }
            }}
          />
        )}
        sx={{
          width: "100%",
          "& .MuiAutocomplete-inputRoot": {
            padding: "0 !important"
          },
          "& .MuiSvgIcon-root": {
            color: "#1b2538"
          },
          "& .MuiAutocomplete-tag": {
            backgroundColor: colors.primaryTint,
            color: colors.primaryDark
          },
          "& .MuiAutocomplete-option": {
            "&.Mui-focused": {
              backgroundColor: colors.primaryTint
            },
            '&[aria-selected="true"]': {
              backgroundColor: colors.primaryTintHover,
              "&.Mui-focused": {
                backgroundColor: colors.primaryTintHover
              }
            }
          }
        }}
        noOptionsText={
          !loadingProjects && (!isRegionSelectionEnabled || formData.region)
            ? "No projects available"
            : ""
        }
      />
      {!loadingProjects && (
        <Typography
          variant="caption"
          sx={{
            ...helperTextBaseStyles,
            color: errors.projectName ? colors.error : colors.textPrimaryDark
          }}
        >
          {errors.projectName}
        </Typography>
      )}
    </Box>
  );
};

export default ProjectField;
